import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import SimpleBar from 'simplebar';
import Lucide from '@/components/Base/Lucide';
import Tippy from '@/components/Base/Tippy';
import Button from '@/components/Base/Button';
import { Dialog } from '@/components/Base/Headless';
import { useAppDispatch } from '@/stores/hooks';
import { setPendingPrompt } from '@/stores/chatSidebarSlice';
import SuggestedPrompts from './SuggestedPrompts';
import { useLocation, useNavigate } from 'react-router-dom';

export interface ChatItem {
  chat_id: string;
  title: string;
  created_date: string | null;
  last_date: string | null;
  message_count: number;
}

function bucketChats(chats: ChatItem[]): { label: string; chats: ChatItem[] }[] {
  const now = new Date();
  const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const today = startOfDay(now);
  const yesterday = today - 86_400_000;
  const sevenDaysAgo = today - 7 * 86_400_000;
  const buckets: Record<string, ChatItem[]> = {
    Today: [],
    Yesterday: [],
    'Last 7 Days': [],
    Older: [],
  };
  for (const c of chats) {
    const t = c.last_date ? new Date(c.last_date).getTime() : 0;
    if (t >= today) buckets.Today.push(c);
    else if (t >= yesterday) buckets.Yesterday.push(c);
    else if (t >= sevenDaysAgo) buckets['Last 7 Days'].push(c);
    else buckets.Older.push(c);
  }
  return Object.entries(buckets)
    .filter(([, list]) => list.length > 0)
    .map(([label, list]) => ({ label, chats: list }));
}

const XL_BREAKPOINT_QUERY = '(min-width: 1280px)';

function useIsAtLeastXl(): boolean {
  const [isXl, setIsXl] = useState<boolean>(() =>
    typeof window !== 'undefined' ? window.matchMedia(XL_BREAKPOINT_QUERY).matches : true,
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mql = window.matchMedia(XL_BREAKPOINT_QUERY);
    const handleChange = (e: MediaQueryListEvent) => setIsXl(e.matches);
    setIsXl(mql.matches);
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, []);

  return isXl;
}

export interface SidebarProps {
  username: string;
  isSuperowner: boolean;
  canDeleteChats?: boolean;
  activeChatId: string;
  onSelectChat: (chatId: string) => void;
  onNewChat: () => void;
  fetchLlm: (path: string, init?: RequestInit) => Promise<Response>;
  onAuthFailure: () => void;
  refreshKey: number;
  compactMenu?: boolean;
}

export default function Sidebar({
  username,
  isSuperowner,
  canDeleteChats,
  activeChatId,
  onSelectChat,
  onNewChat,
  fetchLlm,
  onAuthFailure,
  refreshKey,
  compactMenu,
}: SidebarProps) {
  const dispatch = useAppDispatch();
  const deleteAllowed = isSuperowner || canDeleteChats === true;
  const [chats, setChats] = useState<ChatItem[]>([]);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ chatId: string; title: string } | null>(null);
  const scrollableRef = useRef<HTMLDivElement>(null);
  const simpleBarInstanceRef = useRef<SimpleBar | null>(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const isScheduledActive = pathname.endsWith('/scheduled-report');

  const onAuthFailureRef = useRef(onAuthFailure);
  useEffect(() => {
    onAuthFailureRef.current = onAuthFailure;
  }, [onAuthFailure]);

  const isAtLeastXl = useIsAtLeastXl();
  const effectivelyCompact = Boolean(compactMenu) && isAtLeastXl;

  const fetchChats = useCallback(async () => {
    try {
      const res = await fetchLlm('/chats');
      if (res.status === 401) {
        onAuthFailureRef.current();
        return;
      }
      const data = await res.json();
      setChats(data.chats || []);
    } catch {
      /* ignore */
    }
  }, [fetchLlm]);

  useEffect(() => {
    fetchChats();
  }, [fetchChats, username, refreshKey]);

  useEffect(() => {
    if (scrollableRef.current && !simpleBarInstanceRef.current) {
      simpleBarInstanceRef.current = new SimpleBar(scrollableRef.current);
    }
    return () => {
      simpleBarInstanceRef.current?.unMount();
      simpleBarInstanceRef.current = null;
    };
  }, []);

  const requestDelete = useCallback(
    (chatId: string, title: string) => {
      if (!deleteAllowed) return;
      setDeleteTarget({ chatId, title });
    },
    [deleteAllowed],
  );

  const closeDeleteDialog = useCallback(() => {
    if (deletingId) return; // don't allow closing mid-delete
    setDeleteTarget(null);
  }, [deletingId]);

  const confirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    const { chatId } = deleteTarget;
    setDeletingId(chatId);
    try {
      const res = await fetchLlm(`/chats/${encodeURIComponent(chatId)}`, { method: 'DELETE' });
      if (res.status === 401) {
        onAuthFailureRef.current();
        return;
      }
      if (!res.ok && res.status !== 404) throw new Error(`HTTP ${res.status}`);
      setChats((cs) => cs.filter((c) => c.chat_id !== chatId));
      if (chatId === activeChatId) onNewChat();
      fetchChats();
    } catch {
      fetchChats();
    } finally {
      setDeletingId(null);
      setDeleteTarget(null);
    }
  }, [deleteTarget, fetchLlm, activeChatId, onNewChat, fetchChats]);


  const handleSelectPrompt = useCallback(
    (prompt: string) => {
      dispatch(setPendingPrompt(prompt));
      onNewChat();
    },
    [dispatch, onNewChat],
  );

  const isNewChatActive = useMemo(
    () => !activeChatId || !chats.some((c) => c.chat_id === activeChatId),
    [activeChatId, chats],
  );

  const linkBoxClasses = (isActive: boolean) =>
    clsx([
      'flex items-center rounded-[0.6rem] border border-transparent px-2.5 py-2 mb-1 text-sm cursor-pointer',
      'transition-all duration-200 ease-in',
      isActive
        ? 'bg-white ring-1 ring-slate-200 shadow-[0px_2px_3px_#0000000b] dark:bg-darkmode-400 dark:border-darkmode-300'
        : 'hover:bg-white/80 hover:ring-1 ring-slate-200 hover:shadow-[0px_2px_3px_#0000000b] dark:hover:bg-darkmode-400/80',
    ]);

  const linkTitleClasses = (isActive: boolean) =>
    clsx([
      'overflow-hidden text-ellipsis whitespace-nowrap',
      isActive ? 'text-primary font-medium' : 'text-slate-600 dark:text-slate-300',
    ]);

  const newChatBoxClasses = (isActive: boolean) =>
    clsx([
      'flex items-center rounded-[0.6rem] border border-transparent px-2.5 py-2 mb-1 text-sm cursor-pointer mt-1',
      'transition-all duration-200 ease-in min-w-0',
      isActive
        ? 'bg-slate-100 border-l-[3px] border-l-primary dark:bg-darkmode-400'
        : 'hover:bg-white/80 hover:ring-1 ring-slate-200 hover:shadow-[0px_2px_3px_#0000000b] dark:hover:bg-darkmode-400/80',
    ]);

  const newChatTitleClasses = (isActive: boolean) =>
    clsx([
      'overflow-hidden text-ellipsis whitespace-nowrap min-w-0',
      isActive ? 'font-semibold text-primary' : 'text-slate-600 dark:text-slate-300',
    ]);

  return (
    <div className="relative flex flex-col h-full min-h-0">
      <div
        className={clsx([
          'flex-none flex items-center z-10 px-3 w-[275px] relative duration-300 group-[.side-menu--collapsed]:xl:w-[91px]',
        ])}
      >
        <div className="ml-2 side-menu__divider">
          PULSE
        </div>

      </div>

      <div
        ref={scrollableRef}
        className={clsx([
          'flex-1 min-h-0 w-full z-20 px-5 pb-3',
          '[&_.simplebar-content]:p-0 [&_.simplebar-track.simplebar-vertical]:w-[10px] [&_.simplebar-track.simplebar-vertical]:mr-0.5 [&_.simplebar-track.simplebar-vertical_.simplebar-scrollbar]:before:bg-slate-400/30',
        ])}
      >
        <ul>
          {/* New chat */}
          <li className="">
            {effectivelyCompact ? (
              <Tippy
                as="a"
                href=""
                content="New chat"
                className={clsx([newChatBoxClasses(isNewChatActive), 'py-2.5'])}
                onClick={(event: React.MouseEvent) => {
                  event.preventDefault();
                  onNewChat();
                }}
              >
                <Lucide icon="MessageSquare" className="w-4 h-4 mr-2 flex-none font-semibold stroke-primary stroke-2 text-primary" />
              </Tippy>
            ) : (
              <a
                href=""
                className={newChatBoxClasses(isNewChatActive)}
                onClick={(event) => {
                  event.preventDefault();
                  onNewChat();
                }}
              >
                <Lucide icon="MessageSquare" className="w-4 h-4 mr-2 flex-none font-semibold stroke-primary stroke-2 text-primary" />
                <div className={newChatTitleClasses(isNewChatActive)}>Ask Pulse</div>
              </a>
            )}
          </li>

          {!effectivelyCompact && (
            <>
              <li className="group-[.side-menu--collapsed]:xl:hidden">
                <SuggestedPrompts isSuperowner={isSuperowner} onSelect={handleSelectPrompt} />
              </li>
              <li className="mt-5 whitespace-nowrap text-[11px] font-semibold uppercase tracking-wide text-slate-400 group-[.side-menu--collapsed]:xl:hidden">
                Conversation history
              </li>
              {chats.length === 0 && (
                <li className="flex flex-col items-center gap-2 py-10 px-1 text-center group-[.side-menu--collapsed]:xl:hidden">
                  <div className="w-9 h-9 rounded-full bg-black/[0.03] flex items-center justify-center">
                    <Lucide icon="MessagesSquare" className="w-4 h-4 opacity-40" />
                  </div>
                  <span className="text-[12px] opacity-45">No conversations yet</span>
                </li>
              )}
              <div className="">
                {bucketChats(chats).map((group) => (
                  <li key={group.label}>
                    <ul>
                      <li className="whitespace-nowrap text-xs text-slate-500 mt-3 mb-2">
                        {group.label}
                      </li>

                      {group.chats.map((chat) => {
                        const isActive = chat.chat_id === activeChatId;
                        return (
                          <li key={chat.chat_id} className="group/chat-item relative ">
                            <a
                              href=""
                              title={chat.title}
                              className={`${linkBoxClasses(isActive)} h-8`}
                              onClick={(event) => {
                                event.preventDefault();
                                onSelectChat(chat.chat_id);
                              }}
                            >
                              <div className={clsx([linkTitleClasses(isActive), 'text-[13px]'])}>
                                {chat.title}
                              </div>
                              {deleteAllowed && (
                                <button
                                  type="button"
                                  className="hidden group-hover/chat-item:flex bg-white absolute right-2 top-1/2 -translate-y-1/2 items-center justify-center w-6 h-6 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-40"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    event.preventDefault();
                                    requestDelete(chat.chat_id, chat.title);
                                  }}
                                  disabled={deletingId === chat.chat_id}
                                  title="Delete this conversation"
                                  aria-label={`Delete conversation: ${chat.title}`}
                                >
                                  <Lucide icon="Trash" className="w-3.5 h-3.5" />
                                </button>
                              )}
                            </a>
                          </li>
                        );
                      })}
                    </ul>
                  </li>
                ))}
              </div>

            </>
          )}

        </ul>


      </div>

      <Dialog open={!!deleteTarget} onClose={closeDeleteDialog}>
        <Dialog.Panel>
          <Dialog.Title>
            <span className="text-lg font-medium">Delete chat?</span>
          </Dialog.Title>

          <Dialog.Description>
            <p className="text-sm leading-relaxed text-slate-600">
              {deleteTarget ? (
                <>
                  Are you sure you want to delete <span className="font-medium text-slate-800">&ldquo;{deleteTarget.title}&rdquo;</span>{' '}?
                  This action cannot be undone.
                </>
              ) : (
                'This cannot be undone.'
              )}
            </p>
          </Dialog.Description>

          <Dialog.Footer className="flex items-center justify-end">
            <Button
              type="button"
              variant="outline-secondary"
              onClick={closeDeleteDialog}
              disabled={!!deletingId}
              className="mr-2"
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={confirmDelete}
              disabled={!!deletingId}
              className="flex items-center gap-2"
            >
              <Lucide icon="Trash2" className="w-4 h-4" />
              <span>{deletingId ? 'Deleting...' : 'Delete'}</span>
            </Button>
          </Dialog.Footer>
        </Dialog.Panel>
      </Dialog>

      <div className="absolute bottom-0 left-0 right-0 z-30 bg-white px-5 pt-2 border-t border-slate-200/70">
        {effectivelyCompact ? (
          <Tippy
            as="a"
            href=""
            content="Scheduled reports"
            className={clsx([linkBoxClasses(isScheduledActive), 'mb-0'])}
            onClick={(event: React.MouseEvent) => {
              event.preventDefault();
              navigate('/scheduled-report');
            }}
          >
            <Lucide icon="CalendarClock" className="w-4 h-4 flex-none text-primary" />
          </Tippy>
        ) : (
          <a
            href=""
            className={clsx([linkBoxClasses(isScheduledActive), 'mb-0'])}
            onClick={(event) => {
              event.preventDefault();
              navigate('/scheduled-report');
            }}
          >
            <Lucide icon="CalendarClock" className="w-4 h-4 mr-2 flex-none text-primary" />
            <div className={clsx([linkTitleClasses(isScheduledActive), 'text-[13px]'])}>
              Scheduled reports
            </div>
          </a>
        )}
      </div>
    </div >
  );
}